import { useQuery } from "@tanstack/react-query";
import {
  ExternalLink,
  Loader2,
  Maximize2,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { anatomyApi, type AnatomyResource } from "@/features/anatomy/anatomy.api";
import { cn } from "@/lib/utils";

export function AnatomyPanel({ compact = false }: { compact?: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ["anatomy-resources"],
    queryFn: anatomyApi.resources,
  });
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const active: AnatomyResource | undefined =
    data?.find((r) => r.slug === activeSlug) ?? data?.[0];

  return (
    <div className={cn("space-y-3", fullscreen && "fixed inset-0 z-50 bg-[var(--color-base)] p-6")}>
      {!compact && (
        <header className="space-y-1">
          <div className="flex items-center gap-2 font-[var(--font-display)] text-sm font-bold">
            <ShieldCheck size={14} className="text-[var(--color-blue-soft)]" />
            Anatomía 3D · open-source
          </div>
          <p className="text-[11.5px] text-[var(--color-text-muted)]">
            4 viewers OSS integrados — sin Complete Anatomy ni otros vendors comerciales.
          </p>
        </header>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-[var(--color-text-muted)]">
          <Loader2 size={14} className="animate-spin" /> Cargando recursos...
        </div>
      )}

      {data && data.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {data.map((r) => (
              <button
                key={r.slug}
                type="button"
                onClick={() => setActiveSlug(r.slug)}
                className={cn(
                  "rounded-[var(--radius-md)] border px-3 py-1.5 text-[11px] font-semibold transition-colors",
                  (activeSlug ?? data[0]?.slug) === r.slug
                    ? "border-[var(--color-blue)]/50 bg-[var(--color-blue)]/10 text-[var(--color-text-primary)]"
                    : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-secondary)] hover:border-[var(--color-blue)]/30",
                )}
              >
                {r.title.split(" (")[0]}
              </button>
            ))}
          </div>

          {active && (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="gold" size="sm">
                    {active.license}
                  </Badge>
                  <span className="truncate font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
                    {active.provider.split(" — ")[0]}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFullscreen((f) => !f)}
                  >
                    <Maximize2 size={12} />
                    {fullscreen ? "Salir" : "Fullscreen"}
                  </Button>
                  <a href={active.embed_url} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm">
                      <ExternalLink size={12} />
                      Abrir en tab
                    </Button>
                  </a>
                </div>
              </div>

              <div
                className={cn(
                  "overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-2)]",
                  fullscreen
                    ? "h-[calc(100vh-160px)]"
                    : compact
                      ? "h-[60vh] sm:h-[360px]"
                      : "h-[60vh] sm:h-[520px]",
                )}
              >
                <iframe
                  key={active.slug}
                  src={active.embed_url}
                  title={active.title}
                  className="h-full w-full border-0"
                  loading="lazy"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  referrerPolicy="no-referrer"
                />
              </div>

              {!compact && (
                <p className="text-[12px] text-[var(--color-text-secondary)]">
                  {active.description}
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
